import { createOptionsFrom } from "@/lib/common/Option";

export const FilterFieldSymbols = {
	CardType: "ct",
	CardSubType: "st",
	Class: "c",
	Cost: "cost",
	DefenseValue: "d",
	Foiling: "f",
	PitchValue: "p",
	Talent: "t",
	Power: "a",
	Intellect: "i",
	Life: "l",
	Rarity: "r",
	Set: "s"
} as const;

export type FilterField = keyof typeof FilterFieldSymbols;

export const FilterFieldLabels: Record<FilterField, string> = {
	CardType: "Card Type",
	CardSubType: "Card Subtype",
	Class: "Class",
	Cost: "Cost",
	DefenseValue: "Defense Value",
	Foiling: "Foiling",
	PitchValue: "Pitch Value",
	Talent: "Talent",
	Power: "Power",
	Intellect: "Intellect",
	Life: "Life",
	Rarity: "Rarity",
	Set: "Set"
};

export const FilterFieldOptions = createOptionsFrom(FilterFieldLabels);

export const isFilterFieldNumeric = (filterField: FilterField): boolean => {
	return filterField === "Cost"
		|| filterField === "DefenseValue"
		|| filterField === "PitchValue"
		|| filterField === "Power"
		|| filterField === "Intellect"
		|| filterField === "Life";
};

export const areFilterFieldValuesKnown = (filterField: FilterField): boolean => {
	return filterField === "Set"
		|| filterField === "Rarity"
		|| filterField === "Foiling";
};

export const filterFieldMaxValue = (filterField: FilterField): number => {
	switch (filterField) {
	case "Cost":
		return 15;
	case "DefenseValue":
		return 15;
	case "PitchValue":
		return 3;
	case "Power":
		return 15;
	case "Intellect":
		return 4;
	case "Life":
		return 40;
	default:
		return 0;
	}
};

export const filterFieldMinValue = (filterField: FilterField): number => {
	switch (filterField) {
	case "Intellect":
		return 3;
	case "Life":
		return 1;
	default:
		return 0;
	}
};

export const filterFieldSupportsMultipleOperands = (field: FilterField): boolean => {
	return field === "CardType"
		|| field === "CardSubType"
		|| field === "Class"
		|| field === "Rarity"
		|| field === "Talent"
		|| field === "Set"
		|| field === "Foiling";
};

export const filterFieldToString = (filterField: FilterField): string => {
	switch (filterField) {
	case "CardType":
		return "ct";
	case "CardSubType":
		return "st";
	case "Class":
		return "c";
	case "Cost":
		return "cost";
	case "DefenseValue":
		return "d";
	case "Foiling":
		return "f";
	case "PitchValue":
		return "p";
	case "Talent":
		return "t";
	case "Power":
		return "a";
	case "Intellect":
		return "i";
	case "Life":
		return "l";
	case "Rarity":
		return "r";
	case "Set":
		return "s";
	default:
		return "";
	}
};

export const parseFilterField = (field: string): FilterField | null => {
	switch (field.toLowerCase()) {
	case "cardtype":
	case "type":
	case "ct":
		return "CardType";

	case "cardsubtype":
	case "subtype":
	case "st":
		return "CardSubType";

	case "class":
	case "c":
		return "Class";

	case "cost":
		return "Cost";

	case "defensevalue":
	case "defense":
	case "def":
	case "d":
		return "DefenseValue";

	case "foiling":
	case "finish":
	case "f":
		return "Foiling";

	case "pitchvalue":
	case "pitch":
	case "resource":
	case "p":
		return "PitchValue";

	case "talent":
	case "t":
		return "Talent";

	case "power":
	case "pow":
	case "attackvalue":
	case "attack":
	case "a":
		return "Power";

	case "rarity":
	case "r":
		return "Rarity";

	case "set":
	case "s":
		return "Set";

	case "life":
	case "l":
		return "Life";

	case "i":
	case "intellect":
	case "int":
		return "Intellect";

	default:
		return null;
	}
};

export const getFilterFieldDefaultOperands = (filterField: FilterField): string[] => {
	if (!isFilterFieldNumeric(filterField)) {
		return [];
	}

	return [filterField === "Intellect" ? "4" : `${filterFieldMinValue(filterField)}`];
};
